import { useEffect, useRef, useState } from 'react';

// Generic envelope from SseHub.publish: {type, ts, payload}
export interface SseEvent<P = unknown> {
  type: string;
  ts: string;
  payload: P;
}

export type SseStatus = 'connecting' | 'open' | 'closed';

/**
 * Per-event handler. `any` on the payload generic is intentional — TS function
 * params are contravariant, so a more permissive signature here lets callers
 * narrow the payload type at the call site without unsafe casts.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SseHandler<P = any> = (e: SseEvent<P>) => void;

export interface UseSseOptions {
  /** Map of event name → handler. Names match the second arg to SseHub.publish. */
  handlers: Record<string, SseHandler>;
  /** Pass false to skip opening — useful when you want to gate by auth state. */
  enabled?: boolean;
}

/**
 * Open one EventSource against `/api/stream/{channel}` and dispatch typed
 * events to handlers. Returns the current connection status so the UI can
 * show a "live" or "reconnecting" indicator.
 *
 * The browser's EventSource handles reconnect on its own (default 3s); we
 * just surface status changes here.
 */
export function useSSE(channel: string, { handlers, enabled = true }: UseSseOptions): SseStatus {
  const [status, setStatus] = useState<SseStatus>('closed');
  // Keep the latest handlers in a ref so the effect doesn't reopen on every render.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!enabled) {
      setStatus('closed');
      return;
    }

    setStatus('connecting');
    const url = `/api/stream/${channel}`;
    const es = new EventSource(url, { withCredentials: true });

    es.onopen = () => setStatus('open');
    es.onerror = () => setStatus('connecting'); // browser will auto-retry

    const listeners: Array<[string, EventListener]> = [];
    const register = (name: string) => {
      const fn: EventListener = (raw) => {
        const me = raw as MessageEvent;
        let parsed: SseEvent;
        try {
          parsed = JSON.parse(me.data);
        } catch {
          return;
        }
        handlersRef.current[name]?.(parsed);
      };
      es.addEventListener(name, fn);
      listeners.push([name, fn]);
    };

    register('hello');
    register('heartbeat');
    Object.keys(handlersRef.current).forEach((name) => {
      if (name !== 'hello' && name !== 'heartbeat') register(name);
    });

    return () => {
      listeners.forEach(([name, fn]) => es.removeEventListener(name, fn));
      es.close();
      setStatus('closed');
    };
  }, [channel, enabled]);

  return status;
}
